// ─── Decorative emoji clean-up for non-classic interface styles ─────────────
// Many labels, dropdown options and messages carry decorative emoji in their text
// ("📊 Reports", "⚠ Backdate detected", "✅ Posted"). In Modern (any non-classic
// style) these are removed from the visible text as it is rendered; meaningful
// marks (🔒 locked, ★ rating, ✓ / ✕ status ticks) are kept. Typed values are never
// touched (inputs / text areas / editable areas). Switching back to Classic puts the
// original text back. Nothing is saved — this changes only what is shown on screen.
import { isModernUi } from './uiMode';

const EMO = /(?:[\u{1F300}-\u{1FAFF}]|[☀-⛿]|[✅❌❎⭐⏰⏳⌛✉✏♻])️?(?:‍(?:[\u{1F300}-\u{1FAFF}]|[☀-⛿])️?)*[  ]?/gu;
const HAS = /[\u{1F300}-\u{1FAFF}☀-⛿✅❌❎⭐⏰⏳⌛✉✏♻]/u;
const KEEP = /^(?:🔒|🔓|★|☆)/u;
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'TEXTAREA', 'INPUT', 'CODE', 'PRE', 'NOSCRIPT']);

const original = new WeakMap();   // text node → its text before clean-up
const tracked = new Set();        // to restore on switch back to Classic
let observer = null;

function skip(node) {
  for (let el = node.parentElement; el; el = el.parentElement) {
    if (SKIP_TAGS.has(el.tagName) || el.isContentEditable || el.classList?.contains('ui-keep-emoji')) return true;
    if (el === document.body) break;
  }
  return false;
}

function cleanText(node) {
  const v = node.nodeValue;
  if (!v || !HAS.test(v) || skip(node)) return;
  const nv = v.replace(EMO, m => (KEEP.test(m) ? m : ''));
  if (nv === v) return;
  original.set(node, v); tracked.add(node);
  node.nodeValue = nv;
}

function cleanTree(root) {
  if (!root) return;
  if (root.nodeType === 3) { cleanText(root); return; }
  if (root.nodeType !== 1 || SKIP_TAGS.has(root.tagName)) return;
  const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let n = w.nextNode(); n; n = w.nextNode()) cleanText(n);
}

function start() {
  if (observer || typeof MutationObserver === 'undefined' || !document.body) return;
  cleanTree(document.body);
  observer = new MutationObserver(list => {
    for (const m of list) {
      if (m.type === 'characterData') cleanText(m.target);
      else for (const n of m.addedNodes) cleanTree(n);
    }
    if (tracked.size > 20000) for (const n of tracked) if (!n.isConnected) tracked.delete(n);   // keep memory bounded
  });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true });
}

function stop() {
  if (observer) { observer.disconnect(); observer = null; }
  for (const n of tracked) {
    if (n.isConnected && original.has(n)) n.nodeValue = original.get(n);
  }
  tracked.clear();
}

let _installed = false;
export function installTextClean() {
  if (_installed || typeof window === 'undefined') return;
  _installed = true;
  const sync = () => (isModernUi() ? start() : stop());
  window.addEventListener('barkah-ui-mode', sync);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sync, { once: true });
  else sync();
}
